import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function r2(v) {
  return Math.round(Number(v || 0) * 100) / 100;
}

/**
 * Dialog to add a TEMPORARY manual bill item (not tracked in inventory).
 * Captured: Name, Type, MRP, Discount%, Price, Quantity
 * The parent receives a line object with manual:true and product_id:null.
 */
export default function ManualItemDialog({ open, onOpenChange, onAdd }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [mrp, setMrp] = useState(0);
  const [discountPct, setDiscountPct] = useState(0);
  const [price, setPrice] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!open) return;
    setName("");
    setType("");
    setMrp(0);
    setDiscountPct(0);
    setPrice(0);
    setQuantity(1);
  }, [open]);

  // When MRP or Discount% changes -> recompute price
  const onMrpChange = (v) => {
    const m = Number(v || 0);
    setMrp(m);
    setPrice(r2(m * (1 - Number(discountPct || 0) / 100)));
  };

  const onDiscountChange = (v) => {
    const d = Math.max(0, Math.min(100, Number(v || 0)));
    setDiscountPct(d);
    setPrice(r2(Number(mrp || 0) * (1 - d / 100)));
  };

  // When Price changes -> recompute Discount%
  const onPriceChange = (v) => {
    const p = Number(v || 0);
    setPrice(p);
    const m = Number(mrp || 0);
    setDiscountPct(m > 0 ? r2(((m - p) / m) * 100) : 0);
  };

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Please enter the item name");
      return;
    }
    if (Number(mrp) <= 0) {
      toast.error("MRP must be greater than 0");
      return;
    }
    if (Number(price) < 0) {
      toast.error("Price cannot be negative");
      return;
    }
    if (Number(price) - Number(mrp) > 0.01) {
      toast.error("Price cannot exceed MRP");
      return;
    }
    if (Number(quantity) < 1) {
      toast.error("Quantity must be at least 1");
      return;
    }
    onAdd({
      name: trimmedName,
      type: type.trim(),
      mrp: Number(mrp),
      discountPct: Number(discountPct),
      price: Number(price),
      quantity: Number(quantity),
    });
    onOpenChange(false);
  };

  const totalAmount = r2(Number(price || 0) * Number(quantity || 0));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="manual-item-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            Add Manual Item
          </DialogTitle>
          <DialogDescription>
            One-off item for this bill only. Will NOT be added to inventory or affect stock.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mt-1 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
          <span className="text-xs text-amber-800">
            Manual items are temporary — they appear on the invoice but never touch products, stock, or barcodes.
          </span>
        </div>

        <div className="space-y-3 mt-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Item Name *
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Gift Wrap, Custom Order"
              className="mt-1"
              autoFocus
              data-testid="manual-item-name"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Type / Category
            </Label>
            <Input
              value={type}
              onChange={(e) => setType(e.target.value)}
              placeholder="e.g. Accessory, Service, Custom"
              className="mt-1"
              data-testid="manual-item-type"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                MRP *
              </Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={mrp}
                onChange={(e) => onMrpChange(e.target.value)}
                className="mt-1 font-mono-data"
                data-testid="manual-item-mrp"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Disc %
              </Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={discountPct}
                onChange={(e) => onDiscountChange(e.target.value)}
                className="mt-1 font-mono-data"
                data-testid="manual-item-discount"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Price *
              </Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={price}
                onChange={(e) => onPriceChange(e.target.value)}
                className="mt-1 font-mono-data"
                data-testid="manual-item-price"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Quantity *
            </Label>
            <Input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value || "1")))}
              className="mt-1 font-mono-data w-32"
              data-testid="manual-item-quantity"
            />
          </div>

          <div className="rounded-md border border-[hsl(var(--border))] bg-[#f8f9fb] px-3 py-2 flex justify-between items-center">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Line Total
            </span>
            <span
              className="font-mono-data text-lg font-bold text-slate-900"
              data-testid="manual-item-total"
            >
              ₹{totalAmount.toFixed(2)}
            </span>
          </div>
        </div>

        <DialogFooter className="mt-3 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="manual-item-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="text-white hover:opacity-90"
            style={{ backgroundColor: "#9E6D5B" }}
            data-testid="manual-item-add-btn"
          >
            Add to Bill
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
